import { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import api, { handleRequest } from "@services/axios/apiManager";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { OTP_RESEND_SECS } from "@screen/Kiosk/otpPrefetch";
import { getSendOtpErrorMessage } from "@utils/otpError";
import { isUnauthorized } from "@utils/lineSession";
import { fetchOtpCooldown } from "../../registerApi";
import Screen from "../../components/Screen";
import { OtpContent } from "../../components/Otp";
import { maskPhone } from "../../demoData";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";

interface NpOtpProps {
  phone: string;
  onDone: () => void;
  /** 省略＝這一頁沒有上一步（流程第一頁）→ 不顯示返回鍵。由 Onboarding 的導航堆疊 canGoBack 決定。 */
  onBack?: () => void;
}

/**
 * 手機 OTP 驗證：進入畫面即向後端送出簡訊 OTP；填滿 5 碼即向後端驗證
 * （驗證中 → 驗證成功 → 自動繼續）。成功後把 phoneVerifiedTicket 存進 useKiosk，
 * 供後續 match/by-phone、walk-in 使用。
 */
const NpOtp = ({ phone, onDone, onBack }: NpOtpProps) => {
  const { lineAccessToken } = useLineAuth();
  const { setPhoneVerifiedTicket } = useKiosk();
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  // 簡訊是否真的寄出去了。原本標題「已將驗證碼傳送至…」與下方倒數都是無條件顯示，
  // 發送失敗時會同時出現「已傳送」「58 秒後可重新傳送」和紅字錯誤，三句話互相矛盾且前兩句是假的。
  const [sent, setSent] = useState(false);
  // 重送冷卻到期時間（epoch 秒），一律以後端為準：進頁查一次、送出成功用回傳值、
  // 收到 429 用錯誤帶的剩餘秒數。原本倒數是元件自己從 60 數，與後端無關（見 Resend 註解）。
  const [resendUntil, setResendUntil] = useState<number | undefined>(undefined);
  // 語音冷卻與簡訊分開（後端兩個管道各自計冷卻）：簡訊冷卻中仍要能立刻改用語音，
  // 那正是收不到簡訊的人最需要的時刻。
  const [voiceUntil, setVoiceUntil] = useState<number | undefined>(undefined);
  // 最後一次用哪個管道送出，只影響標題文案。
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");
  const [done, setDone] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [otpResetSignal, setOtpResetSignal] = useState(0); // 驗證失敗清空後把游標移回第一格
  const full = otp.join("").length === 5;
  const sentPhone = useRef<string | null>(null);
  const triedCode = useRef<string | null>(null);

  const sendOtp = async (channel: "sms" | "voice" = "sms") => {
    if (!phone) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    setError("");
    try {
      const res = await api.post("/app-api/line/send-phone-otp", { accessToken: lineAccessToken, phone, channel }, { isAuth: false });
      // 只有簡訊記進 otpSent：那筆記錄是「防止返回再進頁又寄一封付費簡訊」用的，
      // 語音寫進去會讓後續的簡訊自動發送被誤判為已寄過而整個跳過。
      if (channel === "sms") useKiosk.getState().setOtpSent(phone.replace(/\D/g, ""), Date.now());
      setSent(true);
      setLastChannel(channel);
      const secs = Number(res?.data?.data?.resendAvailableInSeconds) || OTP_RESEND_SECS;
      if (channel === "voice") setVoiceUntil(Math.floor(Date.now() / 1000) + secs);
      else setResendUntil(Math.floor(Date.now() / 1000) + secs);
    } catch (e) {
      // 429 會帶剩餘秒數 → 讓倒數接上後端真正的冷卻，而不是從 60 重數
      const left = Number((e as { response?: { data?: { resendAvailableInSeconds?: number } } })?.response?.data?.resendAvailableInSeconds);
      if (Number.isFinite(left) && left > 0) {
        if (channel === "voice") setVoiceUntil(Math.floor(Date.now() / 1000) + left);
        else setResendUntil(Math.floor(Date.now() / 1000) + left);
        setSent(true); // 冷卻中代表先前確實寄出過，畫面應顯示倒數而非錯誤
      }
      // 401 的 session 復原由 axios 攔截器統一處理（apiManager）。這裡不顯示
      // 「登入逾時，請重新開啟頁面後再試」——那句話使用者做不到任何事，
      // 真的失效時頁面正要跳轉，token 其實有效時（例如未註冊）這句話也是誤導。
      if (isUnauthorized(e)) return;
      setError(getSendOtpErrorMessage(e));
    }
  };

  // 進入畫面（或門號變動）時送出 OTP。
  //
  // 「已寄過」的記錄放 store 而非元件內的 ref：返回上一頁會卸載本元件，
  // 再進來就是全新實例、ref 歸零 → 每返回一次就多寄一封付費簡訊。
  // 導航返回堆疊上線後返回變得好用，這條路徑會被頻繁踩到。
  // 同一支門號仍在冷卻時間內就不重寄（使用者手上那封還有效，下方倒數也還在跑）。
  useEffect(() => {
    if (!phone || sentPhone.current === phone) return;
    sentPhone.current = phone;
    const digits = phone.replace(/\D/g, "");
    const { otpSentPhone, otpSentAt } = useKiosk.getState();
    // 冷卻中＝剛剛才寄過（例如返回後再進來），使用者手上那封仍有效 → 不重寄，但畫面照樣算「已傳送」
    if (otpSentPhone === digits && Date.now() - otpSentAt < OTP_RESEND_SECS * 1000) {
      setSent(true);
      return;
    }
    void sendOtp();
  }, [phone]);

  // 進入畫面時向後端查目前冷卻，接續倒數（重整／返回再進來都不會歸零）。
  // 查失敗不影響流程：resendUntil 維持 undefined → 顯示「重新傳送驗證碼」，
  // 真的還在冷卻的話後端會回 429，屆時再接上剩餘秒數。
  useEffect(() => {
    if (!phone || !lineAccessToken) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOtpCooldown(lineAccessToken, phone);
        const secs = Number(res?.data?.data?.resendAvailableInSeconds);
        if (!cancelled && Number.isFinite(secs) && secs > 0) {
          setResendUntil(Math.floor(Date.now() / 1000) + secs);
          setSent(true);
        }
      } catch {
        // 忽略：查冷卻失敗不該擋住驗證流程
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phone, lineAccessToken]);

  // 填滿 5 碼 → 向後端驗證；同一組碼驗證失敗過不重打，需使用者改動後才重試
  useEffect(() => {
    if (!full || done || verifying) return;
    const otpValue = otp.join("");
    if (triedCode.current === otpValue) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    triedCode.current = otpValue;
    setVerifying(true);
    setError("");
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        setPhoneVerifiedTicket(res?.data?.data?.phoneVerifiedTicket || "");
        setDone(true);
      },
      errorCallback: () => {
        setError("驗證碼錯誤，請重新輸入");
        setOtp(["", "", "", "", ""]);
        setOtpResetSignal((n) => n + 1); // 清空後游標回第一格，使用者可直接重打
      },
      finalCallback: () => setVerifying(false),
    });
  }, [full, otp, done, verifying, phone, lineAccessToken]);

  // 顯示成功後短暫停留 → 自動繼續
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => onDone(), 600);
    return () => clearTimeout(t);
  }, [done, onDone]);

  return (
    <Screen
      title="輸入驗證碼"
      subtitle={
        done
          ? "驗證成功"
          : sent
            ? lastChannel === "voice"
              ? `已撥打電話至 ${maskPhone(phone)}`
              : `已將驗證碼傳送至 ${maskPhone(phone)}`
            : error
              ? "驗證碼發送失敗"
              : `正在將驗證碼傳送至 ${maskPhone(phone)}…`
      }
      onBack={onBack}
    >
      {done ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="w-16 h-16 rounded-full bg-[#10B981] flex items-center justify-center onb-rise">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-[16px] font-extrabold text-[#15803D]">驗證成功</div>
        </div>
      ) : (
        <>
          <OtpContent
            otp={otp}
            setOtp={setOtp}
            onResend={() => void sendOtp("sms")}
            onVoice={() => void sendOtp("voice")}
            disabled={verifying}
            resetSignal={otpResetSignal}
            sent={sent}
            resendUntil={resendUntil}
            voiceUntil={voiceUntil}
          />
          {error ? (
            <div className="mt-3 text-center text-[13px] font-semibold text-[#EF4444]">{error}</div>
          ) : (
            <div className="mt-5 text-center text-[13px] text-[#94A3B8]">
              {verifying ? "驗證中…" : lastChannel === "voice" ? `${VOICE_OTP_CALLER_ID} 會來電並唸兩次驗證碼` : "輸入完成後自動驗證"}
            </div>
          )}
        </>
      )}
    </Screen>
  );
};

export default NpOtp;
